import type { KlaviyoOAuthCompleteResponseDto } from '@/apis/backend/generated/models/klaviyoOAuthCompleteResponseDto';

import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, createFileRoute } from '@tanstack/react-router';
import { klaviyoOAuthControllerComplete } from '@/apis/backend/generated/integrations/integrations';
import {
  KLAVIYO_CONNECTED_PARAM,
  recallKlaviyoOAuthReturnTo,
} from '@/components/settings/klaviyo-oauth';
import { KlaviyoOAuthErrorResponseDtoCode } from '@/apis/backend/generated/models/klaviyoOAuthErrorResponseDtoCode';

import { Box, Card, Stack, Button, Typography, CardContent, CircularProgress } from '@mui/material';

import { paths } from 'src/routes/paths';

type CallbackSearch = {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
};

const asString = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

// Klaviyo redirects here after consent. KLAVIYO_OAUTH_REDIRECT_URI on the API
// and the Klaviyo app's redirect allowlist must match this path exactly. It sits
// under _authed, whose sign-in redirect keeps the full query string, so the code
// survives a login detour; it expires five minutes after consent.
export const Route = createFileRoute('/_authed/integrations/klaviyo/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: asString(search.code),
    state: asString(search.state),
    error: asString(search.error),
    error_description: asString(search.error_description),
  }),
  component: KlaviyoOAuthCallbackPage,
});

// The state is single use, so complete must run once per state even when React
// strict mode double-invokes the effect or the page remounts. Every mount for
// the same state awaits the same request.
const completions = new Map<string, Promise<KlaviyoOAuthCompleteResponseDto>>();

function completeOnce(search: CallbackSearch & { state: string }) {
  let pending = completions.get(search.state);
  if (!pending) {
    pending = klaviyoOAuthControllerComplete({
      state: search.state,
      code: search.code,
      error: search.error,
      errorDescription: search.error_description,
    });
    completions.set(search.state, pending);
  }
  return pending;
}

function brandKlaviyoPath(orgId: string, clientId: string) {
  return paths.dashboard.orgSettings(orgId).client.clientIntegrationDetail(clientId, 'klaviyo');
}

function KlaviyoOAuthCallbackPage() {
  const { t } = useTranslation('settings');
  const router = useRouter();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [failure, setFailure] = useState<string | null>(null);

  const errorSentence = (code: string | undefined, serverMessage: string | undefined) => {
    switch (code) {
      case KlaviyoOAuthErrorResponseDtoCode.oauth_state_invalid:
        return t(
          'klaviyoOAuth.errors.stateInvalid',
          'This Klaviyo connection link is no longer valid. Start the connection again from the brand.'
        );
      case KlaviyoOAuthErrorResponseDtoCode.oauth_state_expired:
        return t(
          'klaviyoOAuth.errors.stateExpired',
          'The connection took too long to finish. Start it again.'
        );
      case KlaviyoOAuthErrorResponseDtoCode.oauth_denied:
        return t(
          'klaviyoOAuth.errors.denied',
          'Access was not approved in Klaviyo. An owner, admin or manager on the Klaviyo account has to approve it.'
        );
      case KlaviyoOAuthErrorResponseDtoCode.oauth_exchange_failed:
        return t(
          'klaviyoOAuth.errors.exchangeFailed',
          'Klaviyo could not finish the connection. This can happen when approval takes more than a few minutes.'
        );
      case KlaviyoOAuthErrorResponseDtoCode.klaviyo_account_mismatch:
        return t(
          'klaviyoOAuth.errors.accountMismatch',
          'This brand is connected to a different Klaviyo account. Disconnect it first to switch accounts.'
        );
      case KlaviyoOAuthErrorResponseDtoCode.klaviyo_account_mismatch_upgrade:
      case KlaviyoOAuthErrorResponseDtoCode.klaviyo_account_in_use:
        // The server names the other brand or the reason the account could
        // not be proven the same; its sentence says more than ours could.
        return (
          serverMessage ||
          t('klaviyoOAuth.errors.accountInUse', 'This Klaviyo account is already connected elsewhere.')
        );
      case KlaviyoOAuthErrorResponseDtoCode.klaviyo_oauth_not_configured:
        return t(
          'klaviyoOAuth.errors.notConfigured',
          'Connect with Klaviyo is not available right now. Try again later.'
        );
      default:
        return t(
          'klaviyoOAuth.errors.generic',
          'Something went wrong connecting Klaviyo. Try again.'
        );
    }
  };

  useEffect(() => {
    const { state } = search;
    if (!state) {
      setFailure(errorSentence(KlaviyoOAuthErrorResponseDtoCode.oauth_state_invalid, undefined));
      return undefined;
    }

    let cancelled = false;
    completeOnce({ ...search, state })
      .then((result) => {
        if (cancelled) return;
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/integrations');
          },
        });

        if (result.upgraded) {
          toast.success(
            result.keyLast4
              ? t(
                  'klaviyoOAuth.upgradedWithKey',
                  'Reconnected with Klaviyo. You can now delete the old key pk_...{{last4}} in Klaviyo (Settings > API keys).',
                  { last4: result.keyLast4 }
                )
              : t(
                  'klaviyoOAuth.upgraded',
                  'Reconnected with Klaviyo. You can now delete the old private key in Klaviyo (Settings > API keys).'
                ),
            { duration: 15000 }
          );
        } else {
          toast.success(
            result.klaviyoAccountName
              ? t('klaviyoOAuth.connectedToast', 'Connected to Klaviyo account {{name}}', {
                  name: result.klaviyoAccountName,
                })
              : t('klaviyoOAuth.connectedToastNoName', 'Connected to Klaviyo')
          );
        }

        const destination = new URL(
          result.returnTo || brandKlaviyoPath(result.organizationId, result.clientId),
          window.location.origin
        );
        if (!result.upgraded) {
          destination.searchParams.set(KLAVIYO_CONNECTED_PARAM, result.integrationId);
        }
        router.history.replace(destination.pathname + destination.search + destination.hash);
      })
      .catch((err: { code?: string; message?: string } | undefined) => {
        if (cancelled) return;
        setFailure(errorSentence(err?.code, err?.message));
      });

    return () => {
      cancelled = true;
    };
    // Runs once per state; errorSentence and t are stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.state]);

  const handleTryAgain = () => {
    router.history.replace(recallKlaviyoOAuthReturnTo() ?? paths.dashboard.root);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent>
          {failure ? (
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="h6">
                {t('klaviyoOAuth.failedTitle', 'Klaviyo was not connected')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {failure}
              </Typography>
              <Button variant="contained" onClick={handleTryAgain}>
                {t('klaviyoOAuth.tryAgain', 'Try again')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {t('klaviyoOAuth.finishing', 'Finishing the Klaviyo connection...')}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
